// Detalle de caso — cabecera con nivel, factores, historial y panel de acciones.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, AlertCircle } from 'lucide-react';
import { casosApi } from '../../../lib/dashboardApi';
import { useAuth } from '../../../context/AuthContext';
import { nivelInfo, carrilInfo, ESTADOS } from '../../../lib/dashboardVocab';
import PriorityExplanationModal from './PriorityExplanationModal';
import CaseTimeline from './CaseTimeline';
import CaseActionsPanel from './CaseActionsPanel';
import CaseDetailSkeleton from './CaseDetailSkeleton';
import { track } from '../../../lib/metrics';

const ROLES_FULL     = new Set(['super_admin_inside', 'admin_colegio', 'directivo', 'encargado_convivencia']);
const ROLES_LIMITED  = new Set(['orientador']);

function factoresFromCaso(caso) {
  const f = [];
  if (caso.is_sentinel_activo) f.push('evento_centinela');
  if (caso.nivel_alerta === 'rojo') f.push('caso_rojo_abierto');
  if (caso.nivel_alerta === 'naranjo' && caso.estado === 'pendiente_primera_accion') f.push('naranjo_sin_primera_accion');
  if (caso.proxima_revision && new Date(caso.proxima_revision) < new Date()) f.push('caso_vencido');
  if (caso.carril === 'clinico_orientacion') f.push('carril_clinico');
  if (caso.carril === 'crisis') f.push('carril_crisis');
  if ((caso.n_observaciones || 0) >= 3) f.push('reiteracion_15d');
  return f;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [state, setState] = useState({ caso: null, observaciones: [], seguimientos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const d = await casosApi.detail(id);
      setState({ caso: d.caso, observaciones: d.observaciones || [], seguimientos: d.seguimientos || [] });
      track('caso_open', { nivel: d.caso?.nivel_alerta || 'na', carril: d.caso?.carril || 'na' });
      track('load_time', { view: 'caso_detalle', load_ms: Math.round(performance.now() - t0) });
    } catch (e) {
      setError(e.status === 403 ? 'No tienes acceso a este caso.' : e.message);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <CaseDetailSkeleton />;
  }
  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-[#FADBD8] p-6 flex items-start gap-3" data-testid="caso-error">
        <AlertCircle className="w-5 h-5 text-[#8A1616] shrink-0" />
        <div>
          <p className="text-sm font-bold text-[#8A1616]">{error}</p>
          <button onClick={() => navigate(-1)} className="mt-2 text-xs text-[#5346E6] hover:underline" data-testid="btn-volver-error">
            Volver
          </button>
        </div>
      </div>
    );
  }

  const c = state.caso;
  const n = nivelInfo(c.nivel_alerta);
  const car = carrilInfo(c.carril);
  const factores = factoresFromCaso(c);
  const puedeAsignar = ROLES_FULL.has(profile?.rol);
  const puedeCerrar  = ROLES_FULL.has(profile?.rol);
  const puedeGestionar = ROLES_FULL.has(profile?.rol) || (ROLES_LIMITED.has(profile?.rol) && c.responsable_id === profile?.id);

  return (
    <div className="space-y-5" data-testid="caso-detalle">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-xs text-[#475472] hover:text-[#0B1B5E]"
        data-testid="btn-volver"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      {/* Cabecera */}
      <div className="bg-white rounded-2xl border border-[#E5EAF5] p-5" data-testid="caso-header">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: n.bg, color: n.fg }}
                    data-testid={`caso-nivel-${c.nivel_alerta}`}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: n.dot }} />
                {n.label} · Prioridad {n.prioridad}
              </span>
              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: car.bg, color: car.fg }}
                    data-testid={`caso-carril-${c.carril}`}>
                {car.label}
              </span>
              {c.is_sentinel_activo && (
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-[#FADBD8] text-[#8A1616]" data-testid="caso-centinela-badge">
                  Evento centinela
                </span>
              )}
              <button onClick={() => setExplainOpen(true)} className="text-[11px] text-[#5346E6] hover:underline flex items-center gap-1 ml-auto" data-testid="btn-por-que-nivel">
                <HelpCircle className="w-3.5 h-3.5" />
                ¿Por qué este nivel?
              </button>
            </div>
            <h1 className="text-2xl font-extrabold text-[#0B1B5E]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {c.estudiante || 'Caso grupal / no identificado'}
            </h1>
            <p className="text-sm text-[#475472] mt-1">
              Dominio <strong className="text-[#0B1B5E]">{c.dominio_code}</strong> · Estado <strong className="text-[#0B1B5E]">{ESTADOS[c.estado] || c.estado}</strong>
            </p>
          </div>
        </div>

        {/* Metadatos */}
        <dl className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Creado</dt>
               <dd className="text-[#0B1B5E] mt-0.5" data-testid="meta-creado">{fmtDate(c.creado_en)}</dd></div>
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Primera acción</dt>
               <dd className="text-[#0B1B5E] mt-0.5" data-testid="meta-primera">{c.primera_accion_en ? fmtDate(c.primera_accion_en) : <span className="text-[#8A4200]">Aún sin acción</span>}</dd></div>
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Próximo seguimiento</dt>
               <dd className="text-[#0B1B5E] mt-0.5" data-testid="meta-proxima">{c.proxima_revision || 'No programado'}</dd></div>
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Responsable</dt>
               <dd className="text-[#0B1B5E] mt-0.5" data-testid="meta-responsable">
                 {c.responsable_nombre || <span className="text-[#94A3B8]">Sin asignar</span>}
               </dd></div>
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Observaciones</dt>
               <dd className="text-[#0B1B5E] mt-0.5">{c.n_observaciones || 0}</dd></div>
          <div><dt className="text-[#94A3B8] font-bold uppercase tracking-wide">Modo</dt>
               <dd className="text-[#0B1B5E] mt-0.5 capitalize">{c.modo_registro}</dd></div>
        </dl>

        {/* Factores que explican este nivel */}
        {factores.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#F1F3F9]" data-testid="factores-explican">
            <div className="text-xs font-bold uppercase tracking-wide text-[#475472] mb-2">
              Factores que explican este nivel
            </div>
            <div className="flex flex-wrap gap-1.5">
              {factores.map((f) => (
                <span key={f} className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#EEF0FE] text-[#5346E6]">
                  {factorLabel(f)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-5">
          <CaseTimeline observaciones={state.observaciones} seguimientos={state.seguimientos} />
        </div>
        {/* Acciones */}
        <div className="space-y-3">
          {puedeGestionar ? (
            <CaseActionsPanel
              caso={c}
              puedeAsignar={puedeAsignar}
              puedeCerrar={puedeCerrar}
              puedeGestionar={puedeGestionar}
              onChange={load}
            />
          ) : (
            <div
              className="rounded-2xl border border-[#E4F0FB] bg-[#F3F9FE] p-4 text-xs text-[#0B1B5E]"
              data-testid="acciones-lectura-orientador"
            >
              <p className="font-extrabold text-[#0E5FB9] mb-1">Vista de lectura</p>
              <p className="text-[#475472]">
                Como orientador puedes revisar la información y el historial de este caso.
                La asignación y cierre corresponden al Directivo o Encargado de Convivencia.
              </p>
            </div>
          )}
        </div>
      </div>

      <PriorityExplanationModal
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        nivel={c.nivel_alerta}
        motivos={factores}
      />
    </div>
  );
}

function factorLabel(code) {
  return {
    evento_centinela: 'Evento centinela',
    caso_rojo_abierto: 'Afectación alta / crítica',
    naranjo_sin_primera_accion: 'Sin primera acción',
    caso_vencido: 'Seguimiento vencido',
    reiteracion_15d: 'Reiteración en últimos 15 días',
    carril_clinico: 'Carril clínico/orientación',
    carril_crisis: 'Carril crisis',
  }[code] || code;
}
